import React, { useEffect, useState } from "react";
import SetupStep from "@/components/SetupStep";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * /setup/cuisines — the FIRST focused mini-page in the First Light
 * onboarding tour. Shows the cuisine taxonomy with warm chips; user
 * toggles their family's cuisines and taps "Save & continue tour" to
 * advance to /setup/vendors.
 *
 * Behaviour matches the equivalent section on UseSoon (Family Table)
 * except we don't show sub-regions here — this is the "warm welcome"
 * view, not the detailed one. Sub-regions can be picked later from the
 * Family Table page.
 */
export default function SetupCuisines() {
  const [taxonomy, setTaxonomy] = useState({});
  const [selected, setSelected] = useState([]);
  const [dietType, setDietType] = useState("non_veg");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [tax, hh] = await Promise.all([
          api.get("/cuisines/taxonomy"),
          api.get("/household/me"),
        ]);
        setTaxonomy(tax.data.taxonomy || {});
        setSelected(hh.data.cuisines || []);
        setDietType(hh.data.diet_type || "non_veg");
      } catch {
        // Non-fatal — the empty picker is still functional.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (name) => {
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]
    );
  };

  const saveContinue = async () => {
    try {
      await api.put("/household/me", {
        cuisines: selected,
        diet_type: dietType,
      });
    } catch (err) {
      toast.error("Couldn't save your cuisines. Please try again.");
      throw err;   // cancel navigation
    }
  };

  return (
    <SetupStep
      step={1}
      eyebrow="FIRST LIGHT · STEP 2 OF 5"
      title="Tell us how you cook."
      subtitle="Pick the cuisines your family actually eats — the Family Table uses this so recipe suggestions sound like your kitchen, not a random blog. You can change these any time."
      onSaveContinue={saveContinue}
      nextRoute="/setup/vendors"
    >
      {loading ? (
        <div className="text-sm text-hearth-mute italic">Loading cuisines…</div>
      ) : (
        <>
          {/* Diet type row */}
          <div className="mb-6">
            <div className="caption text-hearth-mute tracking-widest mb-3">Diet</div>
            <div className="flex flex-wrap gap-2">
              {[
                { key: "non_veg", label: "Everything" },
                { key: "vegetarian", label: "Vegetarian" },
                { key: "vegan", label: "Vegan" },
                { key: "pescatarian", label: "Pescatarian" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  data-testid={`setup-diet-${key}`}
                  type="button"
                  onClick={() => setDietType(key)}
                  className={`px-4 py-1.5 rounded-full text-sm border transition ${
                    dietType === key
                      ? "bg-hearth-clay text-white border-hearth-clay"
                      : "bg-white text-hearth-ink border-hearth-line hover:border-hearth-clay/40"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Cuisine chips */}
          <div>
            <div className="caption text-hearth-mute tracking-widest mb-3">
              Cuisines you cook · {selected.length} selected
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(taxonomy).map((name) => {
                const on = selected.includes(name);
                return (
                  <button
                    key={name}
                    data-testid={`setup-cuisine-${name.toLowerCase().replace(/\s+/g, "-")}`}
                    type="button"
                    onClick={() => toggle(name)}
                    className={`px-4 py-2 rounded-full text-sm border transition ${
                      on
                        ? "bg-hearth-clay text-white border-hearth-clay"
                        : "bg-hearth-cream text-hearth-ink border-hearth-line hover:border-hearth-clay/50"
                    }`}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </SetupStep>
  );
}
